import React, {
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import Layout from "../layout/Layout";
import Content from "../layout/Content";
import { useMeCore } from "../../cores/me";
import { boardApiService } from "../../cores/boards/services/api";
import "./WembitoChat.scss";

type ChatRole = "user" | "assistant";

type ChatMessage = {
  id: string;
  role: ChatRole;
  content: string;
};

type CrmChip = {
  type: "opportunity" | "contact" | "company";
  id: string | number;
  label: string;
};

const STORAGE_KEY = (companyId: string) => `wembito-preferred-name:${companyId}`;

const uid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const extractPreferredName = (text: string) => {
  const trimmed = text.trim();
  const patterns = [
    /(?:me llamo|soy|mi nombre es)\s+([A-Za-zÁÉÍÓÚÜÑáéíóúüñ][\wÁÉÍÓÚÜÑáéíóúüñ'’-]{1,30})/i,
    /^([A-Za-zÁÉÍÓÚÜÑáéíóúüñ][\wÁÉÍÓÚÜÑáéíóúüñ'’-]{1,20})$/,
  ];
  for (const pattern of patterns) {
    const match = trimmed.match(pattern);
    if (match?.[1]) return match[1];
  }
  return null;
};

export const WembitoChatScreen = () => {
  const intl = useIntl();
  const { company, user } = useMeCore();
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const userFirstName = String(user?.firstName || "").trim();
  const [preferredName, setPreferredName] = useState<string>("");
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chips, setChips] = useState<CrmChip[]>([]);

  useEffect(() => {
    if (!company?.id) return;
    const stored = localStorage.getItem(STORAGE_KEY(company.id)) || "";
    const name = stored || userFirstName;
    setPreferredName(name);

    const greeting = name
      ? intl.formatMessage(
          { id: "app.wembito.greetingNamed" },
          { name }
        )
      : intl.formatMessage({ id: "app.wembito.greetingAskName" });

    setMessages([
      {
        id: uid(),
        role: "assistant",
        content: greeting,
      },
    ]);
    setChips([]);
    setDraft("");
  }, [company?.id, userFirstName, intl]);

  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, loading]);

  const historyPayload = useMemo(
    () =>
      messages
        .filter((message) => message.role === "user" || message.role === "assistant")
        .slice(-6)
        .map(({ role, content }) => ({ role, content })),
    [messages]
  );

  const sendMessage = useCallback(async () => {
    const text = draft.trim();
    if (!text || !company?.id || loading) return;

    const maybeName = extractPreferredName(text);
    const nextPreferred =
      maybeName || preferredName || userFirstName || "";
    if (maybeName && company.id) {
      localStorage.setItem(STORAGE_KEY(company.id), maybeName);
      setPreferredName(maybeName);
    }

    const userMessage: ChatMessage = {
      id: uid(),
      role: "user",
      content: text,
    };
    setMessages((prev) => [...prev, userMessage]);
    setDraft("");
    setLoading(true);

    try {
      const { payload } = await boardApiService.wembitoChat(
        company.id,
        text,
        historyPayload,
        nextPreferred || undefined
      );

      const reply = String(payload?.reply || "").trim();
      setMessages((prev) => [
        ...prev,
        {
          id: uid(),
          role: "assistant",
          content:
            reply ||
            intl.formatMessage({ id: "app.wembito.emptyReply" }),
        },
      ]);

      const nextChips: CrmChip[] = [];
      for (const item of payload?.results?.opportunities || []) {
        nextChips.push({
          type: "opportunity",
          id: item.id,
          label: item.title || `Opp ${item.id}`,
        });
      }
      for (const item of payload?.results?.contacts || []) {
        nextChips.push({
          type: "contact",
          id: item.id,
          label: item.name || `Contacto ${item.id}`,
        });
      }
      for (const item of payload?.results?.companies || []) {
        nextChips.push({
          type: "company",
          id: item.id,
          label: item.name || `Empresa ${item.id}`,
        });
      }
      setChips(nextChips.slice(0, 8));
    } catch (error: any) {
      const status = error?.response?.status;
      const message =
        status === 403
          ? intl.formatMessage({ id: "app.wembito.errorForbidden" })
          : error?.response?.data?.message ||
            intl.formatMessage({ id: "app.wembito.errorGeneric" });
      toast.error(message);
      setMessages((prev) => [
        ...prev,
        {
          id: uid(),
          role: "assistant",
          content: intl.formatMessage({ id: "app.wembito.errorChat" }),
        },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [
    company?.id,
    draft,
    historyPayload,
    intl,
    loading,
    preferredName,
    userFirstName,
  ]);

  const onSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      sendMessage();
    },
    [sendMessage]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  const startNewChat = useCallback(() => {
    if (!company?.id) return;
    const name =
      localStorage.getItem(STORAGE_KEY(company.id)) ||
      preferredName ||
      userFirstName;
    setPreferredName(name);
    setMessages([
      {
        id: uid(),
        role: "assistant",
        content: name
          ? intl.formatMessage({ id: "app.wembito.greetingNamed" }, { name })
          : intl.formatMessage({ id: "app.wembito.greetingAskName" }),
      },
    ]);
    setChips([]);
    setDraft("");
    inputRef.current?.focus();
  }, [company?.id, intl, preferredName, userFirstName]);

  const suggestions = useMemo(
    () => [
      intl.formatMessage({ id: "app.wembito.suggestion1" }),
      intl.formatMessage({ id: "app.wembito.suggestion2" }),
      intl.formatMessage({ id: "app.wembito.suggestion3" }),
    ],
    [intl]
  );

  return (
    <Layout className="wembito-layout">
      <Content className="wembito-content">
        <div className="wembito-shell">
          <header className="wembito-header">
            <div className="wembito-header__brand">
              <span className="wembito-header__avatar-wrap">
                <img
                  src="/assets/wembito-head.png"
                  alt="Wembito"
                  className="wembito-header__avatar"
                />
              </span>
              <div>
                <h1>{intl.formatMessage({ id: "app.wembito.title" })}</h1>
                <p>{intl.formatMessage({ id: "app.wembito.subtitle" })}</p>
              </div>
            </div>
            <button
              type="button"
              className="wembito-header__new"
              onClick={startNewChat}
              title={intl.formatMessage({ id: "app.wembito.newChat" })}
            >
              <FontAwesomeIcon icon={["fas", "pen"]} />
            </button>
          </header>

          <div className="wembito-messages" ref={listRef}>
            {messages.length <= 1 && (
              <div className="wembito-welcome">
                <div className="wembito-suggestions">
                  {suggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => setDraft(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((message) => (
              <div
                key={message.id}
                className={`wembito-bubble wembito-bubble--${message.role}`}
              >
                {message.role === "assistant" && (
                  <img
                    src="/assets/wembito-head.png"
                    alt=""
                    className="wembito-bubble__avatar"
                  />
                )}
                <div className="wembito-bubble__body">{message.content}</div>
              </div>
            ))}

            {loading && (
              <div className="wembito-bubble wembito-bubble--assistant">
                <img
                  src="/assets/wembito-head.png"
                  alt=""
                  className="wembito-bubble__avatar"
                />
                <div className="wembito-bubble__body wembito-bubble__typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}

            {chips.length > 0 && !loading && (
              <div className="wembito-chips">
                {chips.map((chip) => (
                  <span
                    key={`${chip.type}-${chip.id}`}
                    className={`wembito-chip wembito-chip--${chip.type}`}
                  >
                    {chip.label}
                  </span>
                ))}
              </div>
            )}
          </div>

          <form className="wembito-composer" onSubmit={onSubmit}>
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder={intl.formatMessage({
                id: "app.wembito.placeholder",
              })}
              disabled={loading || !company?.id}
            />
            <div className="wembito-composer__toolbar">
              <span className="wembito-composer__hint">
                {intl.formatMessage({ id: "app.wembito.hint" })}
              </span>
              <button
                type="submit"
                className="wembito-composer__send"
                disabled={loading || !draft.trim() || !company?.id}
                aria-label={intl.formatMessage({ id: "app.wembito.send" })}
              >
                <FontAwesomeIcon icon={["fas", "arrow-up"]} />
              </button>
            </div>
          </form>
        </div>
      </Content>
    </Layout>
  );
};

export default WembitoChatScreen;
